/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */

export const CONSENT_STORAGE_KEY = 'hrm_cookie_consent_v1';
export const CONSENT_VERSION = 1 as const;
export const CONSENT_DURATION_MONTHS = 6;

export type ConsentStatus = 'granted' | 'denied';

export type ConsentSelection = {
  statistics: boolean;
  marketing: boolean;
};

export type ConsentPreferences = ConsentSelection & {
  version: typeof CONSENT_VERSION;
  necessary: true;
  decidedAt: string;
  expiresAt: string;
};

export type GoogleConsentState = {
  analytics_storage: ConsentStatus;
  ad_storage: ConsentStatus;
  ad_user_data: ConsentStatus;
  ad_personalization: ConsentStatus;
};

type ConsentStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const getBrowserStorage = (): ConsentStorage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const removeStoredConsent = (storage: ConsentStorage) => {
  try {
    storage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
};

export const createConsentPreferences = (selection: ConsentSelection, now: Date = new Date()): ConsentPreferences => {
  const expiresAt = new Date(now);
  expiresAt.setMonth(expiresAt.getMonth() + CONSENT_DURATION_MONTHS);

  return {
    version: CONSENT_VERSION,
    necessary: true,
    statistics: selection.statistics,
    marketing: selection.marketing,
    decidedAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };
};

export const mapConsentToGoogle = (selection: ConsentSelection): GoogleConsentState => ({
  analytics_storage: selection.statistics ? 'granted' : 'denied',
  ad_storage: selection.marketing ? 'granted' : 'denied',
  ad_user_data: selection.marketing ? 'granted' : 'denied',
  ad_personalization: selection.marketing ? 'granted' : 'denied',
});

const isConsentPreferences = (value: unknown, now: Date): value is ConsentPreferences => {
  if (!value || typeof value !== 'object') return false;

  const preferences = value as Partial<ConsentPreferences>;
  const decidedAt = typeof preferences.decidedAt === 'string' ? Date.parse(preferences.decidedAt) : Number.NaN;
  const expiresAt = typeof preferences.expiresAt === 'string' ? Date.parse(preferences.expiresAt) : Number.NaN;

  return (
    preferences.version === CONSENT_VERSION &&
    preferences.necessary === true &&
    typeof preferences.statistics === 'boolean' &&
    typeof preferences.marketing === 'boolean' &&
    !Number.isNaN(decidedAt) &&
    !Number.isNaN(expiresAt) &&
    expiresAt > now.getTime()
  );
};

export const readStoredConsent = (
  storage: ConsentStorage | null = getBrowserStorage(),
  now: Date = new Date(),
): ConsentPreferences | null => {
  if (!storage) return null;

  let rawConsent: string | null;

  try {
    rawConsent = storage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }

  if (rawConsent === null) return null;

  try {
    const parsedConsent: unknown = JSON.parse(rawConsent);

    if (isConsentPreferences(parsedConsent, now)) return parsedConsent;
  } catch {
    // Invalid JSON is handled by removing the stored value below.
  }

  removeStoredConsent(storage);
  return null;
};

function gtag(command: 'consent', action: 'update', state: GoogleConsentState) {
  if (command !== 'consent' || action !== 'update' || !state || typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  // Google Tag Manager expects the same Arguments object produced by the standard gtag helper.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export const applyConsentPreferences = (selection: ConsentSelection) => {
  if (typeof window === 'undefined') return;

  gtag('consent', 'update', mapConsentToGoogle(selection));
  window.dataLayer.push({
    event: 'hrm_consent_updated',
    statistics_consent: selection.statistics ? 'granted' : 'denied',
    marketing_consent: selection.marketing ? 'granted' : 'denied',
  });
};

export const updateConsentPreferences = (
  selection: ConsentSelection,
  storage: ConsentStorage | null = getBrowserStorage(),
  now: Date = new Date(),
): ConsentPreferences => {
  const preferences = createConsentPreferences(selection, now);

  if (storage) {
    try {
      storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Apply the choice to the current page even if persistence is unavailable.
    }
  }

  applyConsentPreferences(preferences);
  return preferences;
};

export const restoreStoredConsent = (
  storage: ConsentStorage | null = getBrowserStorage(),
  now: Date = new Date(),
): ConsentPreferences | null => {
  const preferences = readStoredConsent(storage, now);

  if (preferences) applyConsentPreferences(preferences);

  return preferences;
};
